import React, { useEffect } from 'react'
import { Route, Switch, useLocation, useHistory, Redirect } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { observer } from 'mobx-react-lite'

import { useStore } from '../stores/store'
import useMediaQueryMatch from '../hooks/useMediaQueryMatch'
import { applyPageSeo } from '../utils/pageSeo'
import LoadingComponent from './LoadingComponents'
import AppShellLayout from './AppShellLayout'
import AppFooter from './AppFooter'
import TopNavBar from './TopNavBar'
import BottomNavBar from './BottomNavBar'
import PrimarySidebarNav from './PrimarySidebarNav'
import MotdBanner from '../common/ui/MotdBanner'
import PolicyUpdateBanner from '../common/ui/PolicyUpdateBanner'
import ModalContainer from '../common/modals/ModalContainer'
import UploadDrawer from '../../components/UploadDrawer'

import Homepage from '../../features/home/Homepage'
import AboutUsPage from '../../features/about/AboutUsPage'
import LoginForm from '../../features/users/LoginForm'
import RegisterForm from '../../features/users/RegisterForm'
import ForgotPasswordForm from '../../features/users/ForgotPasswordForm'
import ResetPasswordForm from '../../features/users/ResetPasswordForm'
import ConfirmAccountDeletionPage from '../../features/users/ConfirmAccountDeletionPage'
import NotFound from '../../features/errors/NotFound'
import ServerError from '../../features/errors/ServerError'
import TestErrors from '../../features/errors/TestError'

import SlopFeedPage from '../../features/slopfeed/SlopFeedPage'
import SlopLibraryPage from '../../features/slopfeed/SlopLibraryPage'
import SlopEmbedPage from '../../features/slopfeed/SlopEmbedPage'
import SlopPostPage from '../../features/slopfeed/SlopPostPage'
import ShootDetailPage from '../../features/slopfeed/ShootDetailPage'
import BlogDashboard from '../../features/blog/dashboard/BlogDashboard'
import PostDetails from '../../features/blog/details/PostDetails'
import PostForm from '../../features/blog/form/PostForm'
import LegacyFollowingFeedPage from '../../features/feed/LegacyFollowingFeedPage'
import PollsPage from '../../features/polls/PollsPage'
import PollDetailPage from '../../features/polls/PollDetailPage'
import CreatePollPage from '../../features/polls/CreatePollPage'
import CachesPage from '../../features/caches/CachesPage'
import CacheDetailPage from '../../features/caches/CacheDetailPage'
import CreateCachePage from '../../features/caches/CreateCachePage'
import CachePostPage from '../../features/caches/CachePostPage'

import GunVaultPage from '../../features/gunvault/GunVaultPage'
import FirearmDetails from '../../features/firearms/details/FirearmDetails'
import FirearmForm from '../../features/firearms/form/FirearmForm'
import Inventory from '../../features/inventory/Inventory'
import InventoryDetail from '../../features/inventory/InventoryDetail'
import PartsBin from '../../features/parts/PartsBin'
import PartDetail from '../../features/parts/PartDetail'
import RegistryDashboard from '../../features/registry/RegistryDashboard'
import RegistryForm from '../../features/registry/RegistryForm'
import RegistryFullForm from '../../features/registry/RegistryFullForm'
import RegistryModelDetails from '../../features/registry/RegistryModelDetails'
import BuildsDashboard from '../../features/builds/BuildsDashboard'
import BuildForm from '../../features/builds/BuildForm'
import BuildWorkbench from '../../features/builds/BuildWorkbench'

import AmmoDashboard from '../../features/ammo/AmmoDashboard'
import AmmoProductDetail from '../../features/ammo/AmmoProductDetail'
import CartridgeSpecDetail from '../../features/ammo/CartridgeSpecDetail'
import SubmitAmmoPage from '../../features/ammo/SubmitAmmoPage'
import ReloaderForum from '../../features/ammo/ReloaderForum'
import RecipeDetail from '../../features/ammo/RecipeDetail'
import SubmitRecipePage from '../../features/ammo/SubmitRecipePage'
import AmmoHelpPage from '../../features/help/AmmoHelpPage'

import ProfilePage from '../../features/profiles/ProfilePage'
import ProfileEditForm from '../../features/profiles/ProfileEditForm'
import DiscoverPage from '../../features/profiles/DiscoverPage'
import SearchResultsPage from '../../features/search/SearchResultsPage'
import NotificationsPage from '../../features/notifications/NotificationsPage'

import ModerationDashboard from '../../features/moderation/ModerationDashboard'
import AdminUsersPage from '../../features/admin/AdminUsersPage'
import AdminUserDetailPage from '../../features/admin/AdminUserDetailPage'
import AdminContentPage from '../../features/admin/AdminContentPage'
import OverseerPage from '../../features/admin/OverseerPage'
import LegalTextPage from '../../features/legal/LegalTextPage'
import SettingsPage from '../../features/settings/SettingsPage'

import ArduinoDashboard from '../../features/arduinos/dashboard/ArduinoDashboard'
import ArduinoDetails from '../../features/arduinos/dashboard/ArduinoDetails'
import ArduinoForm from '../../features/arduinos/form/ArduinoForm'
import UiShowcase from '../../features/dev/UiShowcase'

function App () {
  const location = useLocation()
  const history = useHistory()
  const { commonStore, userStore, navigationStore, uploadDrawerStore } = useStore()
  const desktopNav = useMediaQueryMatch('(min-width: 769px)')

  useEffect(() => {
    if (commonStore.token) {
      Promise.all([
        userStore.getUser(),
        commonStore.loadMotd(),
        commonStore.loadFeatureFlags(),
        commonStore.loadPendingLegalAcceptances()
      ]).finally(() => commonStore.setAppLoaded())
    } else {
      Promise.all([
        commonStore.loadMotd(),
        commonStore.loadFeatureFlags()
      ]).finally(() => commonStore.setAppLoaded())
    }
  }, [commonStore, userStore])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  useEffect(() => {
    navigationStore.syncPrimaryFromLocation(location.pathname, userStore.user?.username)
  }, [location.pathname, userStore.user?.username, navigationStore])

  useEffect(() => {
    applyPageSeo(location.pathname)
  }, [location.pathname])

  if (!commonStore.appLoaded) {
    return <LoadingComponent content="Loading app..." />
  }

  return (
    <>
      <ToastContainer position="bottom-right" hideProgressBar />
      <ModalContainer />
      <Switch>
        <Route exact path="/" component={Homepage} />
        <Route path="/">
          <>
            <TopNavBar
              onUploadClick={() => uploadDrawerStore.open()}
              onSettingsClick={() => {
                if (userStore.isLoggedIn && userStore.user?.username) {
                  history.push('/settings')
                } else {
                  history.push('/login')
                }
              }}
            />
            <PrimarySidebarNav />
            <BottomNavBar />
            <UploadDrawer />

            <AppShellLayout desktopNav={desktopNav}>
              <MotdBanner />
              <PolicyUpdateBanner />
              <Switch>
                {/* Slop & blog */}
                <Route exact path="/slop-feed" component={SlopFeedPage} />
                <Route exact path="/slop-feed/library" component={SlopLibraryPage} />
                <Route exact path="/slop-feed/embed" component={SlopEmbedPage} />
                <Route exact path="/slop-feed/:id" component={SlopPostPage} />
                <Route exact path="/slop-feed/shoot/:shootId" component={ShootDetailPage} />
                <Route exact path="/blog" component={BlogDashboard} />
                <Route path="/blog/:id" component={PostDetails} />
                <Route key={location.key} path={['/createPost', '/manage/:id']} component={PostForm} />
                <Route exact path="/feed" render={() => <Redirect to="/legacy-following-feed" />} />
                <Route exact path="/legacy-following-feed" component={LegacyFollowingFeedPage} />
                <Route exact path="/polls" component={PollsPage} />
                <Route exact path="/polls/new" component={CreatePollPage} />
                <Route exact path="/polls/:id" component={PollDetailPage} />
                <Route exact path="/caches" component={CachesPage} />
                <Route exact path="/caches/new" component={CreateCachePage} />
                <Route exact path="/caches/:id/posts/:postId" component={CachePostPage} />
                <Route exact path="/caches/:id" component={CacheDetailPage} />

                {/* Vault, inventory, parts */}
                <Route exact path="/vault" render={() => <Redirect to="/gun-vault" />} />
                <Route exact path="/gun-vault" component={GunVaultPage} />
                <Route path="/gun-vault/:id" component={FirearmDetails} />
                <Route key={location.key} path={['/createFirearm', '/manageFirearm/:id']} component={FirearmForm} />
                <Route exact path="/inventory" component={Inventory} />
                <Route path="/inventory/:id" component={InventoryDetail} />
                <Route exact path="/parts-bin" component={PartsBin} />
                <Route exact path="/parts/:id" component={PartDetail} />

                {/* Registry & builds */}
                <Route exact path="/registry" component={RegistryDashboard} />
                <Route exact path="/registry/new" component={RegistryForm} />
                <Route exact path="/registry/full-form" component={RegistryFullForm} />
                <Route path="/registry/:id" component={RegistryModelDetails} />
                <Route exact path="/builds" component={BuildsDashboard} />
                <Route exact path="/builds/new" component={BuildForm} />
                <Route path="/builds/:id/edit" render={({ match }) => <Redirect to={`/builds/${match.params.id}`} />} />
                <Route path="/builds/:username/:slug" component={BuildWorkbench} />
                <Route path="/builds/:id" component={BuildWorkbench} />

                {/* Ammo */}
                <Route exact path="/ammo" component={AmmoDashboard} />
                <Route exact path="/ammo/submit" component={SubmitAmmoPage} />
                <Route path="/help/ammo/:field" component={AmmoHelpPage} />
                <Route path="/ammo/products/:id" component={AmmoProductDetail} />
                <Route path="/ammo/specs/:id" component={CartridgeSpecDetail} />
                <Route exact path="/ammo/reloading" component={ReloaderForum} />
                <Route exact path="/ammo/reloading/new" component={SubmitRecipePage} />
                <Route path="/ammo/reloading/:id" component={RecipeDetail} />

                {/* Profiles & social */}
                <Route
                  exact
                  path="/profile"
                  render={() =>
                    userStore.isLoggedIn && userStore.user
                      ? <Redirect to={`/profiles/${userStore.user.username}`} />
                      : <Redirect to="/login" />
                  }
                />
                <Route exact path="/profiles/:username" component={ProfilePage} />
                <Route path="/profiles/:username/edit" component={ProfileEditForm} />
                <Route path="/discover" component={DiscoverPage} />
                <Route path="/search" component={SearchResultsPage} />
                <Route path="/notifications" component={NotificationsPage} />
                <Route exact path="/settings" component={SettingsPage} />

                {/* Moderation & admin */}
                <Route exact path="/moderation" component={ModerationDashboard} />
                <Route exact path="/admin/users" component={AdminUsersPage} />
                <Route path="/admin/users/:username" component={AdminUserDetailPage} />
                <Route exact path="/admin/overseer" component={OverseerPage} />
                <Route exact path="/admin/content" component={AdminContentPage} />

                {/* Auth & misc */}
                <Route path="/login" component={LoginForm} />
                <Route path="/register" component={RegisterForm} />
                <Route path="/forgot-password" component={ForgotPasswordForm} />
                <Route path="/reset-password" component={ResetPasswordForm} />
                <Route exact path="/confirm-deletion" component={ConfirmAccountDeletionPage} />
                <Route exact path="/terms" render={() => <LegalTextPage legalKey="terms-of-service" />} />
                <Route exact path="/privacy" render={() => <LegalTextPage legalKey="privacy-policy" />} />
                <Route exact path="/guidelines" render={() => <LegalTextPage legalKey="community-guidelines" />} />
                <Route exact path="/about" component={AboutUsPage} />
                <Route exact path="/ui-showcase" component={UiShowcase} />
                <Route path="/errors" component={TestErrors} />
                <Route path="/server-error" component={ServerError} />

                {/* Admin-only Arduino (legacy) */}
                <Route
                  exact
                  path="/arduinos"
                  render={() => (userStore.user?.isAdmin ? <ArduinoDashboard /> : <Redirect to="/" />)}
                />
                <Route
                  path="/arduinos/:id"
                  render={(props) => (userStore.user?.isAdmin ? <ArduinoDetails {...props} /> : <Redirect to="/" />)}
                />
                <Route
                  key={location.hash}
                  path={['/createArduino/', '/editArduino/:id']}
                  render={(props) => (userStore.user?.isAdmin ? <ArduinoForm {...props} /> : <Redirect to="/" />)}
                />

                <Route component={NotFound} />
              </Switch>
              <AppFooter />
            </AppShellLayout>
          </>
        </Route>
      </Switch>
    </>
  )
}

export default observer(App)
